import { elementAttributes } from "../html-attributes";
import {
  modelsAlignment,
  parseTextBlockStyleAttrs,
  wrapsOnlyStyledLink,
} from "../html-parse";
import { EMPTY_STYLE_MAP, type StyleMap } from "../shared-block-styles";
import { EmailBlockDefinition } from "../types";

import { LuType } from "react-icons/lu";

import { ParagraphEmailRenderer } from "./email-renderer";
import { ParagraphTiptapView } from "./tiptap-view";

export interface ParagraphAttrs extends Record<string, unknown> {
  textAlign: "left" | "center" | "right";
  /** Inline styles as the markup wrote them — the block's style store. */
  style: StyleMap;
  /** Attributes the block does not otherwise model, kept rather than dropped. */
  attributes: Record<string, string>;
  /** Named style overrides carried by documents saved before `style` existed. */
  backgroundColor: string;
  fontFamily: string;
  fontSize: string;
  color: string;
  fontWeight: string;
  lineHeight: string;
  textDecoration: string;
  borderRadius: string;
  borderWidth: string;
  borderStyle: string;
  borderColor: string;
  paddingTop: string;
  paddingBottom: string;
  paddingLeft: string;
  paddingRight: string;
  marginTop: string;
  marginBottom: string;
}

export const paragraphBlock: EmailBlockDefinition<ParagraphAttrs> = {
  name: "emailParagraph",
  label: "Paragraph",
  icon: LuType,
  category: "content",

  schema: {
    attrs: {
      textAlign: { default: "left" },
      style: { default: EMPTY_STYLE_MAP },
      attributes: { default: {} },
      backgroundColor: { default: "" },
      fontFamily: { default: "" },
      fontSize: { default: "" },
      color: { default: "" },
      fontWeight: { default: "" },
      lineHeight: { default: "" },
      textDecoration: { default: "" },
      borderRadius: { default: "" },
      borderWidth: { default: "" },
      borderStyle: { default: "" },
      borderColor: { default: "" },
      paddingTop: { default: "" },
      paddingBottom: { default: "" },
      paddingLeft: { default: "" },
      paddingRight: { default: "" },
      marginTop: { default: "" },
      marginBottom: { default: "" },
    },
    content: "inline*",
    group: "emailBlock",
    draggable: true,
  },

  // `p` only — matching bare `div` would swallow every layout wrapper in a
  // designed email and turn the whole thing into one paragraph. Unmatched
  // wrappers are transparent to ProseMirror: their children still get parsed.
  parseHTML: () => [
    {
      tag: "p",
      getAttrs: (node) => {
        const el = node as HTMLElement;
        // A paragraph holding only a button-styled link is the button's
        // wrapper, not a paragraph — decline so the button rule can claim it.
        if (wrapsOnlyStyledLink(el)) return false;
        // `text-align: justify` is an instruction this block has no word for,
        // and answering it with `left` is the composer overruling the design.
        if (!modelsAlignment(el)) return false;
        return {
          ...parseTextBlockStyleAttrs(el),
          attributes: elementAttributes(el, ["align"]),
        };
      },
    },
  ],

  tiptapComponent: ParagraphTiptapView,
  emailRenderer: ParagraphEmailRenderer,
};
